/**
 * Orders Section Constants
 */

import { Clock, Package, CheckCircle, XCircle } from '@spcc/uikit';
import type { OrderStatus, OrdersSortBy } from '../../../domain/types';
import type { OrderTableColumnKey } from './types';

export interface OrderTableColumnConfig {
  key: OrderTableColumnKey;
  labelKey: string;
  sortField?: OrdersSortBy;
  align?: 'start' | 'end';
  width?: string;
  requiresAdmin?: boolean;
  locked?: boolean;
}

export const ORDER_TABLE_COLUMNS_STORAGE_KEY =
  'orders.table.visible-columns.v2';

export const ORDER_TABLE_COLUMNS: OrderTableColumnConfig[] = [
  {
    key: 'orderNumber',
    labelKey: 'orders.id',
    sortField: 'orderNumber',
    locked: true,
  },
  { key: 'sapSalesOrderNumber', labelKey: 'orders.sapSalesOrderNumber' },
  {
    key: 'tenant',
    labelKey: 'common.tenant',
    sortField: 'tenant',
    requiresAdmin: true,
  },
  { key: 'factory', labelKey: 'orders.factory' },
  { key: 'product', labelKey: 'orders.product', sortField: 'product' },
  { key: 'driver', labelKey: 'orders.driver' },
  { key: 'quantity', labelKey: 'orders.quantity', sortField: 'quantity' },
  { key: 'orderDate', labelKey: 'orders.orderDate', sortField: 'orderDate' },
  {
    key: 'deliveryDate',
    labelKey: 'orders.deliveryDate',
    sortField: 'deliveryDate',
  },
  {
    key: 'processingToDeliveredTime',
    labelKey: 'orders.processingToDeliveredTime',
  },
  {
    key: 'loadingTime',
    labelKey: 'orders.loadingTime',
  },

  { key: 'status', labelKey: 'orders.table.status', sortField: 'status' },
  {
    key: 'actions',
    labelKey: 'orders.actions',
    align: 'end',
    width: 'w-[100px]',
    locked: true,
  },
];

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getProductColorStyles(code: string) {
  const safe = code || 'product';
  const hue = hashString(safe) % 360;
  const text = `hsl(${hue} 70% 42%)`;
  const dot = `hsl(${hue} 75% 50%)`;
  const bg = `hsla(${hue} 80% 55% / 0.12)`;
  return { bg, text, dot };
}

export const statusConfig: Record<
  OrderStatus,
  {
    bg: string;
    text: string;
    icon: typeof Clock;
    gradient: string;
    dotColor: string;
  }
> = {
  pending: {
    bg: 'bg-gray-50 dark:bg-gray-500/10',
    text: 'text-gray-700 dark:text-gray-400',
    icon: Clock,
    gradient: 'from-gray-500 to-gray-600',
    dotColor: 'bg-gray-500',
  },
  processing: {
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    text: 'text-amber-700 dark:text-amber-400',
    icon: Package,
    gradient: 'from-amber-500 to-orange-500',
    dotColor: 'bg-amber-500',
  },
  delivered: {
    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
    text: 'text-emerald-700 dark:text-emerald-400',
    icon: CheckCircle,
    gradient: 'from-emerald-500 to-emerald-600',
    dotColor: 'bg-emerald-500',
  },
  cancelled: {
    bg: 'bg-red-50 dark:bg-red-500/10',
    text: 'text-red-700 dark:text-red-400',
    icon: XCircle,
    gradient: 'from-red-500 to-red-600',
    dotColor: 'bg-red-500',
  },
};

const fallbackStatusConfig = {
  bg: 'bg-slate-50 dark:bg-slate-900/40',
  text: 'text-slate-600 dark:text-slate-400',
  icon: Clock,
  gradient: 'from-slate-500 to-slate-600',
  dotColor: 'bg-slate-400',
} as const;

export function normalizeOrderStatus(status: string | null | undefined): OrderStatus | null {
  const normalized = status?.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'canceled') return 'cancelled';
  if (normalized === 'pending' || normalized === 'processing' || normalized === 'delivered' || normalized === 'cancelled') {
    return normalized;
  }
  return null;
}

export function getOrderStatusConfig(status: string | null | undefined) {
  const normalized = normalizeOrderStatus(status);
  return normalized ? statusConfig[normalized] : fallbackStatusConfig;
}
